import { useState, useEffect, useRef } from 'react';
import { Check, Filter, Search, SearchX, X } from 'lucide-react';
import { useAuditStore } from '../../stores/useAuditStore';
import { GROUP_LABELS, GROUP_ORDER, INDUSTRY_FILTERS } from '../../data/platforms';
import { cn } from '@/lib/utils';
import PlatformCard from './PlatformCard';
import { useActivePlatforms } from './activePlatforms';

export default function PlatformGrid() {
  const { connectedSources, selectedIndustry, setIndustry } = useAuditStore();
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const filterRef = useRef<HTMLDivElement>(null);
  const activePlatforms = useActivePlatforms();

  useEffect(() => {
    if (!showFilters) return;
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilters]);

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter by industry
  const industryFiltered = selectedIndustry === 'all'
    ? connectedSources
    : connectedSources.filter((s) => s.suggested.includes(selectedIndustry));

  // Filter by category (multi-select)
  const categoryFiltered = selectedCategories.size === 0
    ? industryFiltered
    : industryFiltered.filter((s) => selectedCategories.has(s.group));

  // Filter by search
  const searched = search.trim()
    ? categoryFiltered.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : categoryFiltered;

  // Active platforms first. The set is runtime-driven (Shopify is gated by the
  // backend's config), so a card that stops being connectable also sinks to the
  // bottom of the grid along with the other "Coming soon" ones.
  const filtered = [...searched].sort((a, b) => {
    const aActive = activePlatforms.has(a.id) ? 0 : 1;
    const bActive = activePlatforms.has(b.id) ? 0 : 1;
    return aActive - bActive;
  });

  return (
    <div>
      {/* Industry tabs sit ON the rule, search and category filter to the right. */}
      <div className="mb-3.5 flex flex-wrap items-end justify-between gap-3 border-b">
        <div className="flex flex-wrap gap-5">
          {INDUSTRY_FILTERS.map((industry) => (
            <button
              key={industry.id}
              onClick={() => setIndustry(industry.id)}
              className={cn(
                'relative cursor-pointer pb-2.5 text-[13px] font-semibold transition-colors',
                'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-t-sm',
                'after:bg-primary after:opacity-0 after:transition-opacity after:content-[""]',
                selectedIndustry === industry.id
                  ? 'text-foreground after:opacity-100'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {industry.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 pb-2">
          <div className="relative w-full shrink-0 sm:w-48">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search..."
              title="Search platforms by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                'h-[30px] w-full rounded-full border bg-card py-0 pl-8 text-xs text-foreground placeholder:text-muted-foreground focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30',
                search ? 'pr-7' : 'pr-3',
              )}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <div ref={filterRef} className="relative shrink-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filter by category"
              className={cn(
                'relative flex size-[30px] cursor-pointer items-center justify-center rounded-full border transition-colors',
                showFilters || selectedCategories.size > 0
                  ? 'border-secondary bg-secondary/10 text-secondary'
                  : 'bg-card text-muted-foreground hover:border-secondary/50 hover:text-foreground',
              )}
            >
              <Filter className="size-3.5" />
              {selectedCategories.size > 0 && (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-secondary text-[9px] font-bold text-secondary-foreground">
                  {selectedCategories.size}
                </span>
              )}
            </button>

            {showFilters && (
              <div className="absolute right-0 top-full z-30 mt-1.5 min-w-[220px] rounded-xl border bg-popover py-1 shadow-lg">
                {GROUP_ORDER.map((key) => {
                  const checked = selectedCategories.has(key);
                  return (
                    <button
                      key={key}
                      className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                      onClick={() => toggleCategory(key)}
                    >
                      <span className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                        checked ? 'border-primary bg-primary text-primary-foreground' : 'bg-card',
                      )}>
                        {checked && <Check className="size-2.5" strokeWidth={3} />}
                      </span>
                      <span className={cn('text-foreground', checked && 'font-medium')}>
                        {GROUP_LABELS[key]}
                      </span>
                    </button>
                  );
                })}
                {selectedCategories.size > 0 && (
                  <div className="border-t px-3 py-2">
                    <button
                      className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => setSelectedCategories(new Set())}
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2">
        {filtered.map((p) => (
          <PlatformCard key={p.id} platform={p} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <SearchX className="mb-3 size-10 text-muted-foreground/40" strokeWidth={1.5} />
          <p className="text-sm font-medium text-muted-foreground">
            No platforms found{search ? ` for "${search}"` : ''}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60">
            Try adjusting your filters or search query
          </p>
          {(search || selectedCategories.size > 0 || selectedIndustry !== 'all') && (
            <button
              onClick={() => { setSearch(''); setSelectedCategories(new Set()); setIndustry('all'); }}
              className="mt-4 h-7 cursor-pointer rounded-full border px-3 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
